import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pmAdminApi } from '../../lib/pmApi';
import { startPortalPreview } from '../../lib/portalPreview';
import { startPmImpersonation } from '../../lib/pmImpersonation';
import { PmViewSideChooser } from '../../components/pm/PmViewSideChooser';
import { PmBillingProfilesModal } from '../../components/pm/PmBillingProfilesModal';
import { useAuth } from '../../contexts/AuthContext';
import { Building, Plus, ShieldCheck, Trash2, Eye, Star, X, UserPlus, Send, ExternalLink } from 'lucide-react';
import PoSettingsForm from '../../components/pm/PoSettingsForm';

const empty = {
  name: '', code: '', primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '',
  billing_email: '', address: '', city: '', state: '', zip: '', auto_approval_limit: 500, notes_internal: '',
  po_settings: {},
};

const ROLE_LABEL = {
  property_manager: 'Property Manager',
  asset_manager: 'Asset Manager',
  maintenance_supervisor: 'Maintenance Supervisor',
};

const PmCompanies = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const canPreview = hasPermission('preview_pm_portal');
  const canFullAccess = ['super_admin', 'admin'].includes(user?.role);
  const [billingCompany, setBillingCompany] = useState(null);

  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [picker, setPicker] = useState(null);      // company whose user-picker is open
  const [noPortal, setNoPortal] = useState(null);   // company with no active portal user

  const load = () => Promise.all([
    pmAdminApi.companies.list(true),
    pmAdminApi.users.list(),
    pmAdminApi.properties.list(),
  ]).then(([co, us, pr]) => { setList(co); setUsers(us); setProperties(pr); });
  useEffect(() => { load(); }, []);

  // Active portal users for a company (disabled OR billing-locked accounts are
  // excluded — the preview engine rejects them, so never offer them here).
  const activeUsersFor = (cid) => users.filter((u) => u.company_id === cid && u.active !== false && u.billing_locked !== true);
  const propNamesFor = (u) => (u.property_ids || [])
    .map((pid) => properties.find((p) => p.id === pid)?.name)
    .filter(Boolean);
  const approvalLimitFor = (c) => Number(c?.auto_approval_limit || 0);

  const previewUser = (u, company) => startPortalPreview({
    id: u.id, name: u.name, email: u.email, role: u.role, context: company?.name || '',
  });

  const fullAccessUser = (u, company) => startPmImpersonation({
    id: u.id, name: u.name, email: u.email, role: u.role, company: company?.name || '',
  });

  const handleViewSide = (c, mode = 'readonly') => {
    const active = activeUsersFor(c.id);
    const act = (u) => (mode === 'fullaccess' ? fullAccessUser(u, c) : previewUser(u, c));
    if (active.length === 0) { setNoPortal(c); return; }
    if (active.length === 1) { act(active[0]); return; }
    setPicker({ ...c, mode });
  };

  const goToAccounts = (c, create) => {
    const q = c ? `?company=${c.id}${create ? '&create=1' : ''}` : '';
    navigate(`/admin/pm/accounts${q}`);
  };

  const save = async () => {
    const clean = (v) => (typeof v === 'string' ? v.trim() : v);
    const body = { ...form, auto_approval_limit: Number(form.auto_approval_limit) || 0 };
    for (const k of Object.keys(body)) {
      if (typeof body[k] === 'string') body[k] = clean(body[k]);
    }
    if (!body.primary_contact_email) delete body.primary_contact_email;
    if (!body.billing_email) delete body.billing_email;
    try {
      if (edit) await pmAdminApi.companies.update(edit, body);
      else await pmAdminApi.companies.create(body);
      setEdit(null); setForm(empty); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(' · ')
        : (detail?.message || detail || e.message || 'Save failed');
      window.alert(`Could not save company:\n${msg}`);
    }
  };
  const startEdit = (c) => { setEdit(c.id); setForm({ ...empty, ...c }); };
  const archive = async (c) => { if (window.confirm(`Archive ${c.name}?`)) { await pmAdminApi.companies.archive(c.id); load(); } };

  return (
    <div data-testid="admin-pm-companies" className="p-6 max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MGMT</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Property Management Companies</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Parent entities (e.g. CHN). Each company groups multiple properties + portal users.</p>
        </div>
        <button data-testid="pm-companies-new" onClick={() => { setEdit(null); setForm(empty); }} className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 rounded font-medium hover:bg-[#F3E5AB] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Company</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Form */}
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3 lg:order-2">
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em]">{edit ? 'EDIT COMPANY' : 'NEW COMPANY'}</p>
          <input data-testid="pm-co-name" placeholder="Name (e.g. Community Housing Network)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <input data-testid="pm-co-code" placeholder="Code (e.g. CHN)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm uppercase" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Primary contact name" value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input placeholder="Phone" value={form.primary_contact_phone} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          </div>
          <input placeholder="Primary email" value={form.primary_contact_email || ''} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <input placeholder="Billing email" value={form.billing_email || ''} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
            <input placeholder="Zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          </div>
          <label className="block">
            <span className="text-[#A3A3A3] text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#D4AF37]" /> Auto-approval limit (company-wide)</span>
            <input data-testid="pm-co-limit" type="number" min="0" value={form.auto_approval_limit} onChange={(e) => setForm({ ...form, auto_approval_limit: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
          </label>
          <textarea placeholder="Internal notes (NexFlow only)" rows={2} value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white text-sm" />
          <PoSettingsForm scope="company" value={form.po_settings || {}} onChange={(po) => setForm({ ...form, po_settings: po })} />
          <button data-testid="pm-co-save" onClick={save} className="w-full bg-[#D4AF37] text-[#0A0A0A] h-10 rounded font-medium hover:bg-[#F3E5AB]">{edit ? 'Save changes' : 'Create company'}</button>
          {edit && <button onClick={() => { setEdit(null); setForm(empty); }} className="w-full text-[#525252] text-xs">Cancel</button>}
        </div>

        {/* List */}
        <div className="lg:col-span-2 lg:order-1 space-y-2">
          {list.map((c) => {
            const active = activeUsersFor(c.id);
            return (
              <div key={c.id} data-testid={`pm-co-row-${c.id}`} className={`bg-[#141414] border border-[#262626] rounded p-4 flex items-center gap-3 ${c.active === false ? 'opacity-50' : ''}`}>
                <div className="w-10 h-10 rounded bg-[#D4AF37]/10 flex items-center justify-center"><Building className="w-5 h-5 text-[#D4AF37]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{c.name} <span className="text-[#525252] text-xs font-mono ml-2">{c.code}</span></p>
                  <p className="text-[#A3A3A3] text-xs">{c.property_count || 0} properties · {c.user_count || 0} portal users · Auto-approve ${approvalLimitFor(c).toLocaleString()}</p>
                </div>
                {(canPreview || canFullAccess) && (
                  <PmViewSideChooser
                    testidBase={`pm-co-view-side-${c.id}`}
                    canPreview={canPreview}
                    canFullAccess={canFullAccess}
                    onReadOnly={() => handleViewSide(c, 'readonly')}
                    onFullAccess={() => handleViewSide(c, 'fullaccess')}
                  />
                )}
                <button data-testid={`pm-co-billing-${c.id}`} onClick={() => setBillingCompany(c)} className="text-[#A3A3A3] hover:text-[#D4AF37] text-xs">Billing</button>
                <button data-testid={`pm-co-edit-${c.id}`} onClick={() => startEdit(c)} className="text-[#A3A3A3] hover:text-[#D4AF37] text-xs">Edit</button>
                <button onClick={() => archive(c)} className="text-[#525252] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
          {list.length === 0 && <p className="text-[#525252]">No companies yet — create one to start.</p>}
        </div>
      </div>

      {/* ── User picker (company has multiple active portal users) ── */}
      {picker && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPicker(null)}>
          <div data-testid="pm-co-user-picker" className="bg-[#141414] border border-[#262626] rounded-lg w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-[#262626]">
              <div>
                <p className={`text-xs font-bold tracking-[0.2em] ${picker.mode === 'fullaccess' ? 'text-orange-400' : 'text-[#D4AF37]'}`}>
                  {picker.mode === 'fullaccess' ? 'OPEN WITH FULL ACCESS' : 'VIEW READ-ONLY'}
                </p>
                <h3 className="text-white font-['Outfit'] text-xl font-bold">{picker.name}</h3>
                <p className="text-[#A3A3A3] text-xs mt-1">
                  {picker.mode === 'fullaccess'
                    ? 'Portal access is per-user. Pick the account to act AS with their exact permissions.'
                    : 'Portal access is per-user. Pick the account whose portal you want to preview (read-only).'}
                </p>
              </div>
              <button onClick={() => setPicker(null)} className="text-[#525252] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {/* Primary shortcut */}
            {activeUsersFor(picker.id).some((u) => u.is_primary_portal_admin) && (
              <div className="px-5 pt-4">
                {(() => {
                  const primary = activeUsersFor(picker.id).find((u) => u.is_primary_portal_admin);
                  const act = () => { setPicker(null); (picker.mode === 'fullaccess' ? fullAccessUser : previewUser)(primary, picker); };
                  return (
                    <button
                      data-testid="pm-co-preview-primary"
                      onClick={act}
                      className={`w-full flex items-center justify-between gap-3 rounded border px-4 py-2.5 ${picker.mode === 'fullaccess' ? 'border-orange-400/40 bg-orange-400/10 hover:bg-orange-400/20' : 'border-[#D4AF37]/40 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20'}`}
                    >
                      <span className={`inline-flex items-center gap-2 text-sm font-medium ${picker.mode === 'fullaccess' ? 'text-orange-300' : 'text-[#D4AF37]'}`}><Star className="w-4 h-4" /> {picker.mode === 'fullaccess' ? 'Open as' : 'Preview as'} Primary Admin — {primary.name}</span>
                      <span className={`text-xs ${picker.mode === 'fullaccess' ? 'text-orange-300' : 'text-[#D4AF37]'}`}>{picker.mode === 'fullaccess' ? 'Full Access →' : 'View Portal →'}</span>
                    </button>
                  );
                })()}
              </div>
            )}

            <div className="p-5 space-y-2">
              {activeUsersFor(picker.id).map((u) => {
                const names = propNamesFor(u);
                return (
                  <div key={u.id} data-testid={`pm-co-picker-user-${u.id}`} className="border border-[#262626] rounded p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                          {u.name}
                          {u.is_primary_portal_admin && <span className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-[#D4AF37]/20 text-[#D4AF37]">PRIMARY</span>}
                          <span className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-green-900/40 text-green-300">ACTIVE</span>
                        </p>
                        <p className="text-[#A3A3A3] text-xs mt-0.5">{u.email}</p>
                        <p className="text-[#A3A3A3] text-xs">{ROLE_LABEL[u.role] || u.role} · Approval limit ${approvalLimitFor(picker).toLocaleString()}</p>
                        <p className="text-[#525252] text-xs mt-0.5">
                          {(u.property_ids || []).length} assigned properties
                          {names.length > 0 && <span className="text-[#A3A3A3]"> — {names.slice(0, 3).join(', ')}{names.length > 3 ? ` +${names.length - 3} more` : ''}</span>}
                        </p>
                      </div>
                      <button
                        data-testid={`pm-co-preview-user-${u.id}`}
                        onClick={() => { setPicker(null); (picker.mode === 'fullaccess' ? fullAccessUser : previewUser)(u, picker); }}
                        className={`shrink-0 text-xs border rounded px-2.5 h-8 inline-flex items-center gap-1.5 ${picker.mode === 'fullaccess' ? 'text-orange-300 border-orange-400/40 hover:bg-orange-400/10' : 'text-cyan-300 border-cyan-700/40 hover:bg-cyan-900/20'}`}
                      ><Eye className="w-3.5 h-3.5" /> {picker.mode === 'fullaccess' ? 'Open as' : 'View Portal as'} {u.name.split(' ')[0]}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── No portal user yet ── */}
      {noPortal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setNoPortal(null)}>
          <div data-testid="pm-co-no-portal" className="bg-[#141414] border border-[#262626] rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-[#262626]">
              <div>
                <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">VIEW THEIR SIDE</p>
                <h3 className="text-white font-['Outfit'] text-xl font-bold">{noPortal.name}</h3>
              </div>
              <button onClick={() => setNoPortal(null)} className="text-[#525252] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-[#A3A3A3] text-sm">This company does not have an active portal account yet. Create a portal user or send an invite before previewing their side.</p>
              <div className="grid gap-2">
                <button data-testid="pm-co-noportal-create" onClick={() => goToAccounts(noPortal, true)} className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] h-10 rounded font-medium hover:bg-[#F3E5AB]"><UserPlus className="w-4 h-4" /> Create Portal User</button>
                <button data-testid="pm-co-noportal-invite" onClick={() => goToAccounts(noPortal, true)} className="w-full inline-flex items-center justify-center gap-2 border border-cyan-700/40 text-cyan-300 h-10 rounded hover:bg-cyan-900/20"><Send className="w-4 h-4" /> Send Invite</button>
                <button data-testid="pm-co-noportal-accounts" onClick={() => goToAccounts(noPortal, false)} className="w-full inline-flex items-center justify-center gap-2 border border-[#262626] text-[#A3A3A3] h-10 rounded hover:text-white"><ExternalLink className="w-4 h-4" /> Go to Portal Accounts</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {billingCompany && <PmBillingProfilesModal company={billingCompany} onClose={() => setBillingCompany(null)} />}
    </div>
  );
};

export default PmCompanies;
